/* eslint-disable */
import React from 'react';

import {
  actions,
  ActionType,
  ColumnResizing,
  defaultColumn,
  ensurePluginOrder,
  HeaderGroup,
  Hooks,
  makePropGetter,
  TableInstance,
  TableState,
  useGetLatest,
  UseResizeColumnsProps,
} from 'react-table';
import { FixedPluginHook } from '../utils';

let passiveSupported: unknown = null;

export function passiveEventSupported() {
  // memoize support to avoid adding multiple test events
  if (typeof passiveSupported === 'boolean') {
    return passiveSupported;
  }

  let supported = false;
  try {
    const options = {
      get passive() {
        supported = true;
        return false;
      },
    };

    (window as any).addEventListener('test', null, options);
    (window as any).removeEventListener('test', null, options);
  } catch (err) {
    supported = false;
  }
  passiveSupported = supported;
  return passiveSupported;
}

export function getFirstDefined(...args: unknown[]) {
  for (let i = 0; i < args.length; i += 1) {
    if (typeof args[i] !== 'undefined') {
      return args[i];
    }
  }

  return undefined;
}

// Default Column
defaultColumn.canResize = true;

// Actions
actions.columnStartResizing = 'columnStartResizing';
actions.columnResizing = 'columnResizing';
actions.columnDoneResizing = 'columnDoneResizing';
actions.resetResize = 'resetResize';
actions.setResizing = 'setResizing';

export const useResize: FixedPluginHook = <T extends object>(hooks: Hooks<T>) => {
  hooks.getResizerProps = [defaultGetResizerProps];
  hooks.getHeaderProps.push({
    style: {
      position: 'relative',
    },
  });
  hooks.stateReducers.push(reducer);
  hooks.useInstance.push(useInstance);
  hooks.useInstanceBeforeDimensions.push(useInstanceBeforeDimensions);
};

const defaultGetResizerProps = <T extends object>(
  props: Partial<UseResizeColumnsProps<T>>,
  meta: { instance: TableInstance; header: HeaderGroup },
) => {
  const { instance, header } = meta;
  const { dispatch } = instance;

  const onResizeStart = (e: MouseEvent, header: HeaderGroup) => {
    const headersToResize = getLeafHeaders(header);
    const headerIdWidths = headersToResize.map((d) => [d.id, d.totalWidth]);

    const clientX = e.clientX;

    const dispatchMove = (clientXPos: number) => {
      dispatch({ type: actions.columnResizing, clientX: clientXPos });
    };
    const dispatchEnd = () => dispatch({ type: actions.columnDoneResizing });

    const handlersAndEvents = {
      mouse: {
        moveEvent: 'mousemove',
        moveHandler: (e: MouseEvent) => dispatchMove(e.clientX),
        upEvent: 'mouseup',
        upHandler: (e: MouseEvent) => {
          document.removeEventListener('mousemove', handlersAndEvents.mouse.moveHandler);
          document.removeEventListener('mouseup', handlersAndEvents.mouse.upHandler);
          dispatchEnd();
        },
      },
    };

    const events = handlersAndEvents.mouse;
    const passiveIfSupported = passiveEventSupported() ? { passive: false } : false;
    (document as any).addEventListener(events.moveEvent, events.moveHandler, passiveIfSupported);
    (document as any).addEventListener(events.upEvent, events.upHandler, passiveIfSupported);

    dispatch({
      type: actions.columnStartResizing,
      columnId: header.id,
      columnWidth: header.totalWidth,
      headerIdWidths,
      clientX,
    });
  };

  return [
    props,
    {
      onMouseDown: (e: MouseEvent) => (e as any).persist() || onResizeStart(e, header),
      style: {
        cursor: 'col-resize',
      },
      draggable: false,
      role: 'separator',
    },
  ];
};
const pluginName = 'useResize';
useResize.pluginName = pluginName;

function reducer<D extends object>(
  state: TableState<D>,
  action: ActionType,
  previousState?: TableState<D>,
  instance?: TableInstance<D>,
): TableState<D> {
  if (action.type === actions.init) {
    return {
      ...state,
      columnResizing: {
        columnWidths: {},
        headerIdWidths: [],
      },
    };
  }

  if (action.type === actions.resetResize) {
    return {
      ...state,
      columnResizing: {
        columnWidths: {},
      },
    } as TableState<D>;
  }

  if (action.type === actions.columnStartResizing) {
    const { clientX, columnId, columnWidth, headerIdWidths } = action;

    return {
      ...state,
      columnResizing: {
        ...state.columnResizing,
        startX: clientX,
        headerIdWidths,
        columnWidth,
        isResizingColumn: columnId,
      },
    };
  }

  if (action.type === actions.columnResizing) {
    const { clientX } = action;
    const { startX, columnWidth, headerIdWidths = [] } = state.columnResizing;

    const deltaX = clientX - (startX ?? 0);
    const percentageDeltaX = deltaX / (columnWidth ?? 1);

    const newColumnWidths = {} as Record<string, number>;

    headerIdWidths.forEach(([headerId, headerWidth]) => {
      newColumnWidths[headerId] = Math.max(headerWidth + headerWidth * percentageDeltaX, 0);
    });

    return {
      ...state,
      columnResizing: {
        ...state.columnResizing,
        columnWidths: {
          ...state.columnResizing.columnWidths,
          ...newColumnWidths,
        },
      },
    };
  }

  if (action.type === actions.columnDoneResizing) {
    return {
      ...state,
      columnResizing: {
        ...state.columnResizing,
        startX: undefined,
        isResizingColumn: undefined,
      },
    };
  }

  if (action.type === actions.setResizing) {
    return {
      ...state,
      columnResizing: {
        ...action.columnResizing,
        startX: undefined,
        isResizingColumn: undefined,
      },
    };
  }
  return state;
}

const useInstanceBeforeDimensions = <D extends object>(instance: any) => {
  const {
    flatHeaders,
    disableResizing,
    getHooks,
    state: { columnResizing },
  } = instance;

  const getInstance = useGetLatest(instance);

  flatHeaders.forEach((header: any) => {
    const canResize = getFirstDefined(
      header.disableResizing === true ? false : undefined,
      disableResizing === true ? false : undefined,
      true,
    );

    header.canResize = canResize;
    header.width = columnResizing.columnWidths[header.id] || header.originalWidth || header.width;
    header.isResizing = columnResizing.isResizingColumn === header.id;

    if (canResize) {
      header.getResizerProps = makePropGetter(getHooks().getResizerProps, {
        instance: getInstance(),
        header,
      });
    }
  });
};

function useInstance<D extends object>(instance: TableInstance<D>) {
  const { plugins, dispatch, autoResetResize = true, columns, state } = instance;

  ensurePluginOrder(plugins, ['useAbsoluteLayout'], pluginName);

  const resetResizing = React.useCallback(() => dispatch({ type: actions.resetResize }), [dispatch]);

  const setResizing = React.useCallback(
    (columnResizing: ColumnResizing) => dispatch({ type: actions.setResizing, columnResizing }),
    [dispatch],
  );

  Object.assign(instance, {
    resetResizing,
    setResizing,
  });
}

function getLeafHeaders(header: any) {
  const leafHeaders = [] as any[];
  const recurseHeader = (header: any) => {
    if (header.columns && header.columns.length) {
      header.columns.map(recurseHeader);
    }
    leafHeaders.push(header);
  };
  recurseHeader(header);
  return leafHeaders;
}
